import PaginationWithState from "components/Pagination/PaginationWithState";
import Spinner from "components/Spinner";
import { setNotification } from "features/Notification/notificationSlice";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import authHeader from "services/auth-header";
import API from "services/axios";
import { SPLIT_STATUSES, addressLabel, parseAddress, statusBadge } from "./splitAddress";

/**
 * Sales -> Split Shipments (user, 2026-11-04): every split shipment across
 * work orders. Splits are created and edited on the work order's
 * "Split Shipments" tab; Open goes there.
 */
export const woSplitTabLink = (woId) =>
  `/work-order/details/all/${woId}?page=1&tab=split-shipments`;

export default function SplitShipmentsList() {
  const dispatch = useDispatch();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.post(
        "/split-shipments/list",
        { page, limit, query, status: status || undefined },
        { headers: authHeader() },
      );
      if (!data?.success) throw new Error(data?.message);
      setRows(data.data || []);
      setTotalPages(data.totalPages || 0);
      setTotal(data.total || 0);
    } catch (err) {
      dispatch(
        setNotification({
          message: err?.response?.data?.message || err.message,
          type: "error",
        }),
      );
    } finally {
      setLoading(false);
    }
  }, [page, limit, query, status, dispatch]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div
      className="px-3 py-3"
      style={{ marginLeft: 260, height: "100vh", overflowY: "auto" }}
    >
      {loading && <Spinner />}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h3 className="mb-0">Split Shipments</h3>
          <small className="text-muted">
            Create splits from a work order's <strong>Split Shipments</strong>{" "}
            tab. {total} total.
          </small>
        </div>
        <form
          className="d-flex gap-1"
          onSubmit={(e) => {
            e.preventDefault();
            setPage(1);
            setQuery(search.trim());
          }}
        >
          <select
            className="form-select"
            style={{ width: 140 }}
            aria-label="Status filter"
            value={status}
            onChange={(e) => {
              setPage(1);
              setStatus(e.target.value);
            }}
          >
            <option value="">All statuses</option>
            {SPLIT_STATUSES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
          <input
            type="search"
            className="form-control"
            placeholder="SP-#, W-#, customer, job, address, tracking..."
            style={{ width: 320 }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button className="btn btn-info" type="submit">
            Search
          </button>
          {query && (
            <button
              type="button"
              className="btn btn-outline-danger"
              onClick={() => {
                setSearch("");
                setQuery("");
                setPage(1);
              }}
            >
              Clear
            </button>
          )}
        </form>
      </div>

      <div className="bg-white rounded p-2">
        <table className="table table-sm table-hover align-middle mb-0">
          <thead className="table-light">
            <tr>
              <th>Split</th>
              <th>Work Order</th>
              <th>Customer / Job</th>
              <th>Ship to</th>
              <th>Carrier / Tracking</th>
              <th>Status</th>
              <th>Ship date</th>
              <th className="text-end">Pcs</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading && (
              <tr>
                <td colSpan={9} className="text-center text-muted py-4">
                  No split shipments{query || status ? " match" : " yet"}.
                </td>
              </tr>
            )}
            {rows.map((r) => {
              const badge = statusBadge(r.status);
              return (
                <tr key={r.id}>
                  <td className="fw-bold">SP-{r.splitId}</td>
                  <td>W-{r.woId}</td>
                  <td>
                    <div>{r.customerName}</div>
                    <small className="text-muted">{r.jobName}</small>
                  </td>
                  <td style={{ maxWidth: 260 }}>
                    <small>{addressLabel(parseAddress(r.shippingAddress))}</small>
                  </td>
                  <td>
                    <div>{r.carrier || "-"}</div>
                    <small className="text-muted">{r.trackingNo || ""}</small>
                  </td>
                  <td>
                    <span className={`badge bg-${badge.badge}`}>{badge.label}</span>
                  </td>
                  <td>{r.shipDate || "-"}</td>
                  <td className="text-end">{r.totalQty}</td>
                  <td className="text-end">
                    <Link
                      to={woSplitTabLink(r.woId)}
                      className="btn btn-sm btn-primary"
                    >
                      Open
                    </Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="d-flex justify-content-between align-items-center mt-2">
        <div>
          Show{" "}
          <select
            className="form-select form-select-sm d-inline-block"
            style={{ width: 80 }}
            value={limit}
            onChange={(e) => {
              setPage(1);
              setLimit(Number(e.target.value));
            }}
          >
            {[10, 20, 50, 100].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>
        <PaginationWithState
          totalPages={totalPages}
          currPage={page}
          setCurrPage={setPage}
        />
      </div>
    </div>
  );
}
